// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { formatDateTime } from "@transformers/date";
import { GSGrid, TooltipDropdown } from "@components";
import actionCreators from "@modules/reporting/actionCreator";
import type { State } from "@store/reducers";
import { getMiLogModal, getMiShareModal } from "@modules/reporting/selectors/modals/index";
import { presentationLogs, shareEmployees } from "@modules/reporting/selectors/reports/mi-reports";
import { isLoading } from "@modules/reporting/selectors/modals";
import { getUserId } from "@modules/account/selectors";
import type {
  MiLogs,
  MySavedPresentations,
} from "@modules/reporting/reducer/reports/mi-reports/types";
import type { EmployeeType } from "@modules/employee/types";
import type { AbsenceReason } from "@modules/shared/reducer/absence-reasons/types";
import MiLogModal from "../mi-log-modal";
import MiShareModal from "../mi-share-modal";

type OwnProps<T> = $Exact<{
  clientId: number,
  presentations: Array<T>,
  disabled?: boolean,
  reasons: Array<AbsenceReason>,
}>;

type PropsFromDispatch = $Exact<{
  toggleAddEditReportModal: typeof actionCreators.ToggleAddEditReportModal,
  toggleMiLogModal: typeof actionCreators.ToggleMiLogModal,
  toggleMiShareModal: typeof actionCreators.ToggleMiShareModal,
  searchEmployees: typeof actionCreators.MiReportsEmployeesToShareSearch,
  sharePresentation: typeof actionCreators.MiReportsSharePresentation,
  clearEmployees: typeof actionCreators.MiReportsEmployeesToShareClear,
  getLogs: typeof actionCreators.GetReportingMiReportsPresentationLogs,
  downloadPresentationFile: typeof actionCreators.DownloadMiReportsPresentation,
}>;

type PropsFromState = $Exact<{
  isLoading: boolean,
  logModal: Object,
  shareModal: Object,
  logs: Array<MiLogs>,
  employees: Array<EmployeeType>,
  currentUserId: number | null,
}>;

type Props<T> = $Exact<{
  ...OwnProps<T>,
  ...PropsFromDispatch,
  ...PropsFromState,
  ...PropsFromLocalize,
}>;

export class MyPresentationsTable extends React.Component<Props<Object>> {
  onToggleEditModal = (source: Object) => () => {
    const { reasons } = this.props;
    const selectedOrganisationalUnitIds = source.data.filters.selectedOrganisationalUnits.map(
      el => el.id,
    );
    const excludedOrganisationalUnitIds = source.data.filters.excludedOrganisationalUnits.map(
      el => el.id,
    );
    const excludeAbsenceReasons =
      source.data.filters.excludeAbsenceReasons && source.data.filters.excludeAbsenceReasons.length
        ? source.data.filters.excludeAbsenceReasons
        : (source.data.filters.excludedAbsenceReasons || []).map(el =>
            reasons.find(item => item.name === el),
          );

    const data = {
      ...source.data,
      filters: {
        ...source.data.filters,
        selectedOrganisationalUnitIds,
        excludedOrganisationalUnitIds,
        excludeAbsenceReasons,
      },
    };
    this.props.toggleAddEditReportModal({
      data,
      getNewData: source.data.status !== "NOT_SHARED",
      visible: true,
    });
  };

  onToggleLogModal = (report: Object) => () => {
    this.props.toggleMiLogModal({ report });
  };

  onToggleMiShareModal = (report: Object) => () => {
    this.props.toggleMiShareModal({ report });
  };

  get columnsConfig() {
    let cols = [
      {
        key: "id",
        name: this.props.t("ID"),
      },
      {
        key: "name",
        align: "left",
        name: this.props.t("PRESENTATION_NAME"),
      },
      {
        key: "createdAt",
        name: this.props.t("DATE_CREATED"),
        renderCell: ({ row }: { row: MySavedPresentations }) => formatDateTime(row.createdAt),
      },
      {
        key: "createdBy",
        name: this.props.t("CREATED_BY"),
        align: "left",
        renderCell: ({ row }: { row: MySavedPresentations }) => (
          <span>
            {row.createdBy.firstName} {row.createdBy.lastName}
          </span>
        ),
      },
      {
        key: "hierarchyArea",
        align: "left",
        name: this.props.t("HIERARCHY_AREA"),
        renderCell: ({ row }: { row: MySavedPresentations }) => {
          const areaNames =
            row.filters.selectedOrganisationalUnits &&
            row.filters.selectedOrganisationalUnits.map(el => el.name);
          if (areaNames && areaNames.length) {
            return areaNames.join(", ");
          }

          return "N/A";
        },
      },
      {
        key: "absenceType",
        align: "left",
        name: this.props.t("ABSENCE_TYPE"),
        renderCell: ({ row }: { row: MySavedPresentations }) => {
          if (row.filters.absenceType === "MEDICAL") {
            return "Medical";
          }

          if (row.filters.absenceType === "NON_MEDICAL") {
            return "Non-Medical";
          }

          return "All";
        },
      },
      {
        key: "sharedAt",
        name: this.props.t("DATE_SHARED"),
        renderCell: ({ row }: { row: MySavedPresentations }) =>
          row.sharedAt ? formatDateTime(row.sharedAt) : <span>{this.props.t("N/A")}</span>,
      },
      {
        key: "download",
        name: (
          <TooltipDropdown description={this.props.t("DOWNLOAD_TOOLTIP")} id={"DOWNLOAD"}>
            {this.props.t("DOWNLOAD")}
          </TooltipDropdown>
        ),
        align: "center",
        width: 80,
        renderCell: ({ row }: { row: MySavedPresentations }) =>
          row.presentationFile && !row.presentationFile.isArchived ? (
            <span
              className="material-icons font-size-26 pointer"
              onClick={this.downloadPresentation(row.id, row.presentationFile)}
            >
              file_download
            </span>
          ) : (
            <span className="material-icons font-size-26 text-color-grey">file_download</span>
          ),
      },
      {
        key: "edit",
        name: (
          <TooltipDropdown description={this.props.t("EDIT_TOOLTIP")} id={"EDIT"}>
            {this.props.t("EDIT")}
          </TooltipDropdown>
        ),
        width: 50,
        align: "center",
        renderCell: ({ row }: { row: MySavedPresentations }) =>
          row.presentationFile ? (
            <span
              className="material-icons font-size-26 pointer"
              onClick={this.onToggleEditModal({
                data: row,
              })}
            >
              settings
            </span>
          ) : (
            <span className="material-icons font-size-26 text-color-grey">settings</span>
          ),
      },
      {
        key: "shareAction",
        width: 50,
        name: (
          <TooltipDropdown description={this.props.t("SHARE_TOOLTIP")} id={"SHARE"}>
            {this.props.t("SHARE")}
          </TooltipDropdown>
        ),
        renderCell: ({ row }: { row: MySavedPresentations }) =>
          row.presentationFile ? (
            <span
              className="material-icons font-size-26 pointer"
              onClick={this.onToggleMiShareModal(row)}
            >
              social_distance
            </span>
          ) : (
            <span className="material-icons font-size-26 text-color-grey">social_distance</span>
          ),
        align: "center",
      },
      {
        key: "logAction",
        width: 130,
        name: (
          <div className="d-flex flex-column align-items-center">
            <div className="pr-2">
              <TooltipDropdown description={this.props.t("LOG_TOOLTIP")} id={"LOG"}>
                {this.props.t("LOG")}
              </TooltipDropdown>
            </div>
          </div>
        ),
        renderCell: ({ row }: { row: MySavedPresentations }) => {
          if (
            !row.status ||
            row.status === "NOT_SHARED" ||
            row.status === "GENERATION_IN_PROGRESS"
          ) {
            return "N/A";
          } else {
            return GSGrid.render.statusPill(
              this.props.t(row.status || "OK"),
              this.onToggleLogModal(row),
              "fs-10 w-auto text-color-white px-1",
            );
          }
        },
        align: "center",
      },
    ];

    return cols;
  }

  downloadPresentation = (reportId: number, file: Object) => (e: Object) => {
    e.preventDefault();
    const { clientId } = this.props;
    const { id, name } = file;

    clientId &&
      reportId &&
      id &&
      name &&
      this.props.downloadPresentationFile({
        clientId,
        reportId,
        fileId: id,
        fileName: name,
      });
  };

  render() {
    return (
      <React.Fragment>
        <div className="formatted-grid-wrapper w-100 p-0">
          <GSGrid
            columns={this.columnsConfig}
            rows={this.props.presentations}
            noResultsText={this.props.t("NO_PRESENTATIONS_TEXT")}
          />
        </div>
        {this.props.logModal.visible && (
          <MiLogModal
            data={this.props.logModal}
            getLogs={this.props.getLogs}
            logs={this.props.logs}
            toggleMiLogModal={this.props.toggleMiLogModal}
            clientId={this.props.clientId}
          />
        )}
        {this.props.shareModal.visible && (
          <MiShareModal
            toggleShareModal={this.props.toggleMiShareModal}
            clientId={this.props.clientId}
            modalData={this.props.shareModal}
            employees={this.props.employees}
            getLogs={this.props.getLogs}
            logs={this.props.logs}
            currentUserId={this.props.currentUserId}
            onEmployeesSearch={this.props.searchEmployees}
            onSharePresentation={this.props.sharePresentation}
            clearEmployees={this.props.clearEmployees}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  logModal: getMiLogModal(state),
  shareModal: getMiShareModal(state),
  logs: presentationLogs(state),
  employees: shareEmployees(state),
  isLoading: isLoading(state),
  currentUserId: getUserId(state),
});

const mapDispatchToProps = {
  toggleAddEditReportModal: actionCreators.ToggleAddEditReportModal,
  toggleMiLogModal: actionCreators.ToggleMiLogModal,
  toggleMiShareModal: actionCreators.ToggleMiShareModal,
  searchEmployees: actionCreators.MiReportsEmployeesToShareSearch,
  sharePresentation: actionCreators.MiReportsSharePresentation,
  getLogs: actionCreators.GetReportingMiReportsPresentationLogs,
  clearEmployees: actionCreators.MiReportsEmployeesToShareClear,
  downloadPresentationFile: actionCreators.DownloadMiReportsPresentation,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(MyPresentationsTable);
